import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Space, message, Empty, Tag, Popconfirm, List, Typography, Descriptions, Skeleton } from 'antd';
import { ArrowRightOutlined, DeleteOutlined, EditOutlined, QuestionCircleOutlined, FileOutlined, LinkOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { faqsApi, faqCategoriesApi, normalizeFaqApiBody, faqDescriptionToPlainText } from '../../utils/api';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';

const { Text, Link } = Typography;

const isImageAttachment = (f) => {
  const name = (f?.filename || f?.hashname || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|svg|bmp|ico)(\?|$)/i.test(name) || /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i.test(String(f?.file_url || ''));
};

const SuperadminFaqShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/settings/superadmin-faqs', { state: buildGoBackState() });
  const [loading, setLoading] = useState(true);
  const [faq, setFaq] = useState(null);
  const [fileActionId, setFileActionId] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await faqCategoriesApi.getCreateMeta();
        const root = normalizeFaqApiBody(res);
        if (!cancelled) setCategoryOptions(root.categories || []);
      } catch {
        if (!cancelled) setCategoryOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await faqsApi.show(id);
      const root = normalizeFaqApiBody(res);
      setFaq(root.faq || null);
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت اطلاعات');
      setFaq(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const categoryName = useMemo(() => {
    const fromFaq = faq?.category?.name || faq?.faq_category?.name;
    if (fromFaq) return fromFaq;
    const cid = faq?.faq_category_id;
    if (cid == null) return null;
    const c = categoryOptions.find((x) => String(x.id) === String(cid));
    return c?.name || null;
  }, [faq, categoryOptions]);

  const coverImageSrc = useMemo(() => {
    if (!faq) return null;
    if (faq.image_url) return faq.image_url;
    if (faq.image && /^https?:\/\//i.test(String(faq.image))) return faq.image;
    return null;
  }, [faq]);

  const onDeleteFile = async (fileId) => {
    setFileActionId(fileId);
    try {
      await faqsApi.deleteFile(fileId);
      message.success('فایل حذف شد');
      load();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف فایل');
    } finally {
      setFileActionId(null);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="custom-font w-full min-w-0 p-4 sm:p-6" style={{ fontFamily: 'CustomFont' }}>
          <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <Skeleton.Avatar active size={48} shape="square" className="flex-shrink-0" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton.Input active style={{ width: 'min(100%, 220px)', height: 26 }} />
                  <Skeleton.Input active size="small" style={{ width: 'min(100%, 140px)', height: 18 }} />
                </div>
              </div>
              <div className="flex flex-shrink-0 flex-wrap gap-2">
                <Skeleton.Button active style={{ width: 120 }} />
                <Skeleton.Button active style={{ width: 100 }} />
              </div>
            </div>
            <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
              <Card className="border border-gray-100 dark:border-gray-700 dark:bg-gray-800">
                <Skeleton.Image active className="!w-full max-w-md" style={{ width: '100%', maxWidth: 360, height: 200 }} />
                <Skeleton className="mt-6" active paragraph={{ rows: 5 }} title={false} />
                <div className="mt-6 space-y-3">
                  <Skeleton.Input active style={{ width: 100, height: 16 }} />
                  <Skeleton active paragraph={{ rows: 2 }} title={false} />
                </div>
              </Card>
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (!faq) {
    return (
      <MainLayout>
        <div className="custom-font p-6" style={{ fontFamily: 'CustomFont' }}>
          <Empty description="سوال یافت نشد" />
          <Button className="mt-4" onClick={goBackToList}>
            بازگشت
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-900/25">
                <QuestionCircleOutlined className="text-xl text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl truncate">{faq.title || '—'}</h1>
                <div className="mt-1 flex flex-wrap gap-2 items-center">
                  {categoryName ? <Tag color="blue">{categoryName}</Tag> : <Tag>—</Tag>}
                  <Text type="secondary">#{faq.id}</Text>
                </div>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ArrowRightOutlined />} onClick={goBackToList}>
                بازگشت به لیست
              </Button>
              <Button
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(`/settings/superadmin-faqs/${faq.id}/edit`, {
                    state: { returnTo: returnToRef.current, ...buildGoBackState() },
                  })
                }
              >
                ویرایش
              </Button>
            </Space>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <Descriptions
                bordered
                size="small"
                column={{ xs: 1, sm: 1, md: 2 }}
                className="mb-6 overflow-x-auto"
                labelStyle={{ fontFamily: 'CustomFont', whiteSpace: 'nowrap' }}
                contentStyle={{ fontFamily: 'CustomFont' }}
                items={[
                  { key: 'id', label: 'شناسه', children: faq.id ?? '—' },
                  { key: 'cat', label: 'دسته‌بندی', children: categoryName ? `${categoryName} (${faq.faq_category_id ?? '—'})` : faq.faq_category_id ?? '—' },
                  // { key: 'img', label: 'فیلد image', children: faq.image != null && faq.image !== '' ? String(faq.image) : '—' },
                ]}
              />

              {coverImageSrc ? (
                <div className="mb-6 min-w-0">
                  <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>تصویر </span>
                    <Link href={coverImageSrc} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs">
                      <LinkOutlined /> باز کردن
                    </Link>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-900/30">
                    <img
                      src={coverImageSrc}
                      alt=""
                      className="mx-auto block max-h-72 w-full max-w-2xl object-contain"
                      loading="lazy"
                    />
                  </div>
                </div>
              ) : (
                <div className="mb-6 rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-3 py-4 text-center text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-900/20 dark:text-gray-400">
                  تصویر اصلی ثبت نشده است
                </div>
              )}

              <div className="text-xs text-gray-500 mb-2">توضیحات</div>
              <div className="rounded-xl border border-gray-100 bg-white/70 p-3 sm:p-4 dark:border-gray-700 dark:bg-gray-900/20">
                <div className="whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200 text-sm" style={{ fontFamily: 'CustomFont' }}>
                  {faqDescriptionToPlainText(faq.description) || '—'}
                </div>
              </div>

              <div className="mt-6 min-w-0">
                <div className="text-xs text-gray-500 mb-2">فایل‌های پیوست</div>
                <List
                  dataSource={faq.files || []}
                  locale={{ emptyText: 'فایلی ثبت نشده است' }}
                  renderItem={(f) => (
                    <List.Item
                      key={f.id}
                      className="!flex-col !items-stretch gap-3 sm:!flex-row sm:!items-center"
                      actions={[
                        <Popconfirm key="del" title="حذف فایل؟" okText="حذف" cancelText="لغو" onConfirm={() => onDeleteFile(f.id)}>
                          <Button type="text" danger icon={<DeleteOutlined />} loading={fileActionId === f.id} aria-label="حذف فایل" />
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        className="min-w-0 flex-1"
                        avatar={
                          isImageAttachment(f) && f.file_url ? (
                            <a href={f.file_url} target="_blank" rel="noreferrer" className="inline-flex flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
                              <img src={f.file_url} alt="" className="h-14 w-14 object-cover sm:h-16 sm:w-16" loading="lazy" />
                            </a>
                          ) : (
                            <FileOutlined className="text-xl text-gray-400" />
                          )
                        }
                        title={
                          <Link href={f.file_url} target="_blank" rel="noreferrer" className="break-all">
                            {f.filename || `file-${f.id}`}
                          </Link>
                        }
                        description={
                          <div className="space-y-1 text-xs">
                            <Text type="secondary">{f.size ? `${Number(f.size).toLocaleString('fa-IR')} بایت` : '—'}</Text>
                            {f.file_url ? (
                              <div className="break-all text-gray-400" dir="ltr">
                                {f.file_url}
                              </div>
                            ) : null}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SuperadminFaqShow;
